// Renders a Bill Note written in the rich-text editor (Functional Settings >
// Bill Notes) as formatted text on prints and views.
//
// The notes are stored as HTML. Printing them as text showed the tags
// ("<p><strong>Note</strong>...") on the bill; rendering them raw would let
// anything pasted into the editor (scripts, event handlers) run on the print
// page. This keeps simple formatting only and drops everything else.

const ALLOWED_TAGS = new Set([
    'P', 'BR', 'DIV', 'SPAN', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'SUB', 'SUP',
    'UL', 'OL', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'A', 'HR',
]);
// Removed together with everything inside them.
const DROP_WITH_CONTENT = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'TEMPLATE', 'NOSCRIPT', 'SVG', 'MATH']);

const looksLikeHtml = (s) => /<\/?[a-z][\s\S]*?>/i.test(s) || /&[a-z#0-9]+;/i.test(s);

export const sanitizeNoteHtml = (html) => {
    if (typeof window === 'undefined' || !window.DOMParser) return '';
    const doc = new window.DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstChild;

    const clean = (node) => {
        [...node.childNodes].forEach((child) => {
            if (child.nodeType === 3) return; // text
            if (child.nodeType !== 1) { child.remove(); return; } // comments etc.
            const tag = child.tagName;
            if (DROP_WITH_CONTENT.has(tag)) { child.remove(); return; }
            clean(child);
            if (!ALLOWED_TAGS.has(tag)) {
                // Unknown tag: keep its text, drop the tag.
                child.replaceWith(...child.childNodes);
                return;
            }
            // No attributes survive except a safe link target.
            const href = tag === 'A' ? child.getAttribute('href') : null;
            [...child.attributes].forEach((a) => child.removeAttribute(a.name));
            if (href && /^(https?:|mailto:|tel:)/i.test(href.trim())) {
                child.setAttribute('href', href.trim());
                child.setAttribute('target', '_blank');
                child.setAttribute('rel', 'noopener noreferrer');
            }
        });
    };
    clean(root);
    return root.innerHTML;
};

/** Plain text of a note (tags removed, entities decoded, whitespace tidied). */
export const noteToPlainText = (html) => {
    if (!html) return '';
    if (typeof window === 'undefined' || !window.DOMParser) return String(html).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    const doc = new window.DOMParser().parseFromString(`<div>${String(html).replace(/<(br|\/p|\/div|\/li)\b[^>]*>/gi, '$&\n')}</div>`, 'text/html');
    doc.body.querySelectorAll([...DROP_WITH_CONTENT].join(',').toLowerCase()).forEach((el) => el.remove());
    return (doc.body.textContent || '').replace(/[ \t ]+/g, ' ').replace(/\n\s*\n+/g, '\n').trim();
};

/** True when the note has visible text (an editor can leave "<p><br></p>"). */
export const hasNoteContent = (html) => !!noteToPlainText(html);

const NoteHtml = ({ html, style, className }) => {
    if (!html || !hasNoteContent(html)) return null;
    const text = String(html);
    const baseStyle = { overflowWrap: 'anywhere', ...style };
    if (!looksLikeHtml(text)) {
        return <div className={className} style={{ whiteSpace: 'pre-line', ...baseStyle }}>{text}</div>;
    }
    return (
        <div
            className={`note-html ${className || ''}`}
            style={baseStyle}
            // Sanitised above: formatting tags only, no attributes but safe links.
            dangerouslySetInnerHTML={{ __html: sanitizeNoteHtml(text) }}
        />
    );
};

export default NoteHtml;
